import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../api/auth-api';
import { useAuth } from '../hooks/use-auth';

const MIN_LENGTH = 8;

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const forced = user?.mustChangePassword === true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setBannerError(null);

    if (newPassword.length < MIN_LENGTH) {
      setFieldError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await changePassword({ newPassword });
      // Re-fetch /me so the route guards see `mustChangePassword=false` and
      // don't bounce us back here on the next navigation.
      await refreshUser();
      navigate('/scan');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not change password.';
      setBannerError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <h2 className="text-3xl font-bold text-center mb-2">Change Password</h2>
      <p className="text-center text-gray-400 mb-8" data-testid="change-password-subtitle">
        {forced
          ? 'For security, please set a new password before continuing.'
          : 'Set a new password for your account.'}
      </p>

      {bannerError && (
        <div
          role="alert"
          data-testid="change-password-banner-error"
          className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4"
        >
          {bannerError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="change-password-form" noValidate>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium mb-2">
            New password
          </label>
          <input
            id="new-password"
            data-testid="change-password-new-input"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={MIN_LENGTH}
            autoComplete="new-password"
            className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
            placeholder="At least 8 characters"
          />
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium mb-2">
            Confirm new password
          </label>
          <input
            id="confirm-password"
            data-testid="change-password-confirm-input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={MIN_LENGTH}
            autoComplete="new-password"
            className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
            placeholder="Re-enter your new password"
          />
          {fieldError && (
            <p
              role="alert"
              data-testid="change-password-field-error"
              className="mt-2 text-xs text-red-400"
            >
              {fieldError}
            </p>
          )}
        </div>

        <button
          type="submit"
          data-testid="change-password-submit-button"
          disabled={isSubmitting}
          className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed rounded font-medium transition-colors"
        >
          {isSubmitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}
